import { useCallback, useEffect, useRef, useState } from "react"
import { useAudioLevelObserver } from "@daily-co/daily-react"

/** Audio level (0 to 1) above which a participant counts as speaking. */
export const SPEAKING_LEVEL_THRESHOLD = 0.02
/** How long the speaking state is held after the level drops, so pauses between words do not flicker. */
export const SPEAKING_HOLD_MS = 350

export type SpeakingState = {
  speaking: boolean
  /** Smoothed audio level, 0 to 1, for a live meter (0 while quiet). */
  level: number
}

/** Maps Daily's level (quiet speech sits around 0.05 to 0.2) onto the meter's 0 to 1 range. */
export const meterLevel = (level: number) => Math.min(1, Math.max(0, level * 4))

/**
 * Whether a participant is speaking right now and how loud, from Daily's audio level observer
 * (sampled every 100 ms). Tiles show the level as a live three-bar meter, so the badge moves
 * with the voice rather than on a fixed animation.
 */
export function useSpeakingState(sessionId: string, { threshold = SPEAKING_LEVEL_THRESHOLD, holdMs = SPEAKING_HOLD_MS } = {}): SpeakingState {
  const [speaking, setSpeaking] = useState(false)
  const [level, setLevel] = useState(0)
  const timerRef = useRef<number | null>(null)

  const onLevel = useCallback((rawLevel: number) => {
    const level = meterLevel(rawLevel)
    setLevel((current) => {
      // Quick to rise, slower to fall, so the meter follows syllables without flickering.
      const next = level > current ? level : current * 0.6 + level * 0.4
      return next < 0.02 ? 0 : Math.round(next * 100) / 100
    })
    if (rawLevel >= threshold) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      setSpeaking(true)
      return
    }
    if (timerRef.current) return
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      setSpeaking(false)
      setLevel(0)
    }, holdMs)
  }, [holdMs, threshold])

  useAudioLevelObserver(sessionId, onLevel)

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
  }, [])

  return { speaking, level }
}
